import React, { useEffect, useState } from 'react';
import { BadgeCheck, Building2, Search, Shield, User } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';

interface UserProfileSummary {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
}

interface CompanySummary {
  id: string;
  name: string | null;
  created_at: string;
}

interface CompanyRoleSummary {
  company_id: string;
  company_name: string | null;
  role: string;
  created_at: string | null;
}

export default function AdminManagement() {
  const [adminUsers, setAdminUsers] = useState<UserProfileSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserProfileSummary[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserProfileSummary | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [createdCompanies, setCreatedCompanies] = useState<CompanySummary[]>([]);
  const [companyRoles, setCompanyRoles] = useState<CompanyRoleSummary[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadAdminUsers = async () => {
    const { data, error } = await supabase
      .from('admins')
      .select('user_id, user_profiles(id, email, first_name, last_name)')
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[AdminManagement] Failed to load admin users:', error);
      return;
    }

    const mapped = (data || [])
      .map((record) => record.user_profiles as UserProfileSummary)
      .filter(Boolean);
    setAdminUsers(mapped);
  };

  useEffect(() => {
    loadAdminUsers();
  }, []);

  const handleSearch = async () => {
    const query = searchTerm.trim();
    if (!query) return;

    setIsSearching(true);
    setErrorMessage(null);
    setSelectedUser(null);
    setSearchResults([]);

    const { data, error } = await supabase
      .from('user_profiles')
      .select('id, email, first_name, last_name')
      .or(`email.ilike.%${query}%,first_name.ilike.%${query}%,last_name.ilike.%${query}%`)
      .limit(20);

    if (error) {
      console.error('[AdminManagement] Search failed:', error);
      setErrorMessage('Failed to search users.');
    } else {
      setSearchResults(data || []);
    }

    setIsSearching(false);
  };

  const loadUserDetails = async (user: UserProfileSummary) => {
    setSelectedUser(user);
    setIsLoadingDetails(true);
    setErrorMessage(null);

    try {
      const { data: adminRecord } = await supabase
        .from('admins')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      setIsAdmin(!!adminRecord);

      const { data: companyData } = await supabase
        .from('companies')
        .select('id, name, created_at')
        .eq('created_by', user.id)
        .order('created_at', { ascending: false });

      setCreatedCompanies(companyData || []);

      const { data: relationshipsData } = await supabase
        .from('user_company_relationships')
        .select('company_id, role_id, created_at')
        .eq('user_id', user.id);

      const companyIds = (relationshipsData || []).map((rel) => rel.company_id);
      const roleIds = (relationshipsData || []).map((rel) => rel.role_id).filter(Boolean);

      let companiesMap = new Map<string, string | null>();
      if (companyIds.length) {
        const { data: companies } = await supabase
          .from('companies')
          .select('id, name')
          .in('id', companyIds);
        companiesMap = new Map((companies || []).map((company) => [company.id, company.name]));
      }

      let rolesMap = new Map<string, string>();
      if (roleIds.length) {
        const { data: roles } = await supabase
          .from('system_roles')
          .select('system_role_id, role')
          .in('system_role_id', roleIds);
        rolesMap = new Map((roles || []).map((role) => [role.system_role_id, role.role]));
      }

      const mappedRoles: CompanyRoleSummary[] = (relationshipsData || []).map((rel) => ({
        company_id: rel.company_id,
        company_name: companiesMap.get(rel.company_id) ?? null,
        role: rolesMap.get(rel.role_id || '') || 'unknown',
        created_at: rel.created_at,
      }));

      setCompanyRoles(mappedRoles);
    } catch (error) {
      console.error('[AdminManagement] Failed to load user details:', error);
      setErrorMessage('Failed to load user details.');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const toggleAdmin = async () => {
    if (!selectedUser) return;
    setErrorMessage(null);

    if (isAdmin) {
      const { error } = await supabase
        .from('admins')
        .delete()
        .eq('user_id', selectedUser.id);

      if (error) {
        console.error('[AdminManagement] Failed to remove admin:', error);
        setErrorMessage('Failed to remove admin access.');
        return;
      }
      setIsAdmin(false);
      await loadAdminUsers();
    } else {
      const { error } = await supabase
        .from('admins')
        .insert({ user_id: selectedUser.id });

      if (error) {
        console.error('[AdminManagement] Failed to grant admin:', error);
        setErrorMessage('Failed to grant admin access.');
        return;
      }
      setIsAdmin(true);
      await loadAdminUsers();
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-[#EBFFEE] mb-2">Admin Access</h1>
        <p className="text-[#A9A3A3]">Search users, grant admin access, and review employee credentials.</p>
      </div>

      <div className="bg-[#221F1F] border border-[#504A4A] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Search className="w-5 h-5 text-rollops-accent" />
          Find a user
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <input
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search by email, first name, or last name"
            className="flex-1 rounded-lg bg-[#1A1818] border border-[#504A4A] px-4 py-3 text-[#EBFFEE]"
          />
          <Button
            type="button"
            className="bg-[#9B1421] hover:bg-[#7A0F1A] text-[#EBFFEE]"
            onClick={handleSearch}
            disabled={isSearching}
          >
            {isSearching ? 'Searching...' : 'Search'}
          </Button>
        </div>
        {errorMessage && <p className="text-sm text-[#F87171]">{errorMessage}</p>}
        {searchResults.length > 0 && (
          <div className="border border-[#504A4A] rounded-xl overflow-hidden">
            {searchResults.map((user) => (
              <button
                key={user.id}
                onClick={() => loadUserDetails(user)}
                className="w-full text-left px-4 py-3 border-b border-[#504A4A] hover:bg-[#363232] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <User className="w-4 h-4 text-rollops-accent" />
                  <div>
                    <div className="text-[#EBFFEE]">
                      {user.first_name || user.last_name
                        ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                        : 'Unnamed user'}
                    </div>
                    <div className="text-xs text-[#A9A3A3]">{user.email || 'No email'}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-[#221F1F] border border-[#504A4A] rounded-2xl p-6 space-y-4">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <Shield className="w-5 h-5 text-rollops-accent" />
          Current admins
        </div>
        {adminUsers.length === 0 ? (
          <div className="text-sm text-[#A9A3A3]">No admin users found.</div>
        ) : (
          <div className="grid md:grid-cols-2 gap-3">
            {adminUsers.map((user) => (
              <div key={user.id} className="bg-[#1A1818] border border-[#504A4A] rounded-xl p-4">
                <div className="text-[#EBFFEE]">
                  {user.first_name || user.last_name
                    ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                    : 'Unnamed user'}
                </div>
                <div className="text-xs text-[#A9A3A3]">{user.email || 'No email'}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedUser && (
        <div className="bg-[#221F1F] border border-[#504A4A] rounded-2xl p-6 space-y-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xl font-semibold text-[#EBFFEE]">
                {selectedUser.first_name || selectedUser.last_name
                  ? `${selectedUser.first_name || ''} ${selectedUser.last_name || ''}`.trim()
                  : 'Unnamed user'}
              </div>
              <div className="text-sm text-[#A9A3A3]">{selectedUser.email || 'No email'}</div>
            </div>
            <Button
              type="button"
              className={`${isAdmin ? 'bg-[#363232]' : 'bg-[#9B1421]'} hover:bg-[#7A0F1A] text-[#EBFFEE]`}
              onClick={toggleAdmin}
              disabled={isLoadingDetails}
            >
              <Shield className="w-4 h-4 mr-2" />
              {isAdmin ? 'Remove admin access' : 'Grant admin access'}
            </Button>
          </div>

          {isLoadingDetails ? (
            <div className="text-[#A9A3A3]">Loading credentials...</div>
          ) : (
            <>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-[#1A1818] border border-[#504A4A] rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm text-[#A9A3A3] mb-2">
                    <Shield className="w-4 h-4 text-rollops-accent" />
                    Credentials
                  </div>
                  <div className="flex items-center gap-2">
                    {isAdmin ? (
                      <span className="inline-flex items-center gap-1 text-green-400 text-sm">
                        <BadgeCheck className="w-4 h-4" />
                        Admin
                      </span>
                    ) : (
                      <span className="text-[#A9A3A3] text-sm">Not an admin</span>
                    )}
                  </div>
                </div>
                <div className="bg-[#1A1818] border border-[#504A4A] rounded-xl p-4">
                  <div className="flex items-center gap-2 text-sm text-[#A9A3A3] mb-2">
                    <Building2 className="w-4 h-4 text-rollops-accent" />
                    Gyms created
                  </div>
                  {createdCompanies.length > 0 ? (
                    <ul className="space-y-2 text-sm text-[#EBFFEE]">
                      {createdCompanies.map((company) => (
                        <li key={company.id} className="flex justify-between">
                          <span>{company.name || 'Unnamed gym'}</span>
                          <span className="text-[#706A6A]">
                            {new Date(company.created_at).toLocaleDateString()}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="text-sm text-[#A9A3A3]">No gyms created yet.</div>
                  )}
                </div>
              </div>

              <div className="bg-[#1A1818] border border-[#504A4A] rounded-xl p-4">
                <div className="flex items-center gap-2 text-sm text-[#A9A3A3] mb-3">
                  <BadgeCheck className="w-4 h-4 text-rollops-accent" />
                  Company roles
                </div>
                {companyRoles.length > 0 ? (
                  <div className="space-y-2 text-sm text-[#EBFFEE]">
                    {companyRoles.map((role) => (
                      <div key={`${role.company_id}-${role.role}`} className="flex justify-between">
                        <span>{role.company_name || 'Unknown gym'}</span>
                        <span className="text-[#A9A3A3] capitalize">{role.role}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-[#A9A3A3]">No company roles assigned.</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
